import { useState, useEffect } from "react";
import {
  getInvitedEmployees,
  createEmployeeInvite,
  removeEmployee,
} from "../../services/operations/adminAPI";
import toast from "react-hot-toast";
import { 
  UserPlus, 
  UserMinus, 
  Users, 
  Mail, 
  User, 
  X,
  Loader2,
  Trash2,
  Loader,
  Plus
} from "lucide-react";

const MyTeamPage = () => {
  const [employees, setEmployees] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  // Fetch employees
  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await getInvitedEmployees();
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle adding a new employee
  const handleAddEmployee = async () => {
    if (!newEmployee.firstName || !newEmployee.lastName || !newEmployee.email) {
      toast.error("Please fill all fields!");
      return;
    }

    setLoading(true);
    try {
      const res = await createEmployeeInvite(newEmployee);
      if (!res) toast.loading("Loading...");
      toast.success("Invitation sent!");
      setShowAddModal(false);
      setNewEmployee({ firstName: "", lastName: "", email: "" });
      fetchEmployees();
    } catch (error) {
      toast.error("Failed to send invitation");
    } finally {
      setLoading(false);
    }
  };

  // Handle remove employee
  const handleRemoveClick = (employee) => {
    setSelectedEmployee(employee);
    setShowConfirmModal(true);
  };

  const confirmRemoveEmployee = async () => {
    if (!selectedEmployee) return;

    try {
      await removeEmployee(selectedEmployee._id);
      toast.success("Employee removed!");
      setEmployees(employees.filter((e) => e._id !== selectedEmployee._id));
      setShowConfirmModal(false);
    } catch (error) {
      toast.error("Failed to remove employee");
    }
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-16 w-16 text-blue-600 mx-auto animate-spin" />
        
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Modern Header */}
        <div className="flex flex-col md:flex-row md:justify-between items-center gap-2 mb-6">
          <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
              <Users className="h-6 w-6 text-white" />
            </div>
        
          <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            User Management
          </h1>
          </div>
          <p className=" ml-12 text-slate-600 text-lg">Manage your Users and invitations</p>
          </div>
        

        {/* Add Employee Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3 transform hover:scale-105 font-semibold"
          >
            <UserPlus className="h-5 w-5" />
            Add Employee
          </button>
        </div>
      </div>
        {/* Team Table */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-blue-50/50 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
            <div className="p-6 border-b border-white/20 bg-gradient-to-r from-slate-50/50 to-white/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-slate-600 to-gray-700 rounded-xl shadow-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Users</h2>
                  <p className="text-slate-600 text-sm mt-1">Manage your Users and send invitations</p>
                </div>
              </div>
            </div>
            
            {employees.length === 0 ? (
              <div className="text-center py-16">
                <div className="p-6 bg-slate-100 rounded-2xl w-fit mx-auto mb-6">
                  <Users className="h-16 w-16 text-slate-400 mx-auto" />
                </div>
                <p className="text-slate-500 text-xl mb-2">No employees under you.</p>
                <p className="text-slate-400">Add your first team member to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/20">
                    {employees.map((emp) => (
                      <tr
                        key={emp._id}
                        className="hover:bg-white/50 transition-all duration-300 group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                            <span className="text-slate-800 font-semibold">
                              {emp.firstName} {emp.lastName}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-slate-400" />
                            <span className="text-slate-600">{emp.email}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-center">
                            <button
                              onClick={() => handleRemoveClick(emp)}
                              className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white p-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                              title="Remove Employee"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Add Employee Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/90 to-blue-50/90 rounded-2xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8 w-full max-w-md">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                      <UserPlus className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">Add Employee</h3>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5 text-slate-500" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter first name"
                      value={newEmployee.firstName}
                      onChange={(e) =>
                        setNewEmployee({ ...newEmployee, firstName: e.target.value })
                      }
                      className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter last name"
                      value={newEmployee.lastName}
                      onChange={(e) =>
                        setNewEmployee({ ...newEmployee, lastName: e.target.value })
                      }
                      className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="Enter email address"
                      value={newEmployee.email}
                      onChange={(e) =>
                        setNewEmployee({ ...newEmployee, email: e.target.value })
                      }
                      className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-lg transition-all duration-300"
                    />
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 bg-gradient-to-r from-slate-500 to-gray-600 hover:from-slate-600 hover:to-gray-700 text-white py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddEmployee}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-500 text-white py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 font-semibold disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin h-5 w-5" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="h-5 w-5" />
                        Add Employee
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remove Employee Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/90 to-red-50/90 rounded-2xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8 w-full max-w-md">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl shadow-lg">
                    <UserMinus className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Confirm Removal</h3>
                </div>

                <p className="text-slate-600 mb-2">
                  Are you sure you want to remove{" "}
                  <span className="font-semibold text-slate-800">
                    {selectedEmployee?.firstName} {selectedEmployee?.lastName}
                  </span>{" "}
                  from your team?
                </p>
                <p className="text-sm text-slate-500 mb-6">This action cannot be undone.</p>

                <div className="flex gap-4">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="flex-1 bg-gradient-to-r from-slate-500 to-gray-600 hover:from-slate-600 hover:to-gray-700 text-white py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmRemoveEmployee}
                    className="flex-1 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTeamPage;